import React, { useEffect, useState } from 'react'
import { Alert, Spinner } from 'react-bootstrap'
import '../../components/kyc/kycStyle.css'
import { validateIdNumber } from '../../utils/Validation'
import FooterButtons from '../../components/form/FooterButtons/FooterButtons'
import {
  API_ERROR_MESSAGE,
  INVALID_ID_WARNING,
  PRODUCT_ID,
  PRICELIST,
  RESTRICTED_REPORT,
} from '../../utils/Constants'
import FormInput from '../../components/form/FormInput/FormInput'
import FormControlWithToolTip from '../../components/kyc/FormControlWithToolTip'
import ScreenLayout from '../../components/layouts/ScreenLayout'
import { useMutation } from 'react-query'
import { DownloadFaceViewReport, LoadReport } from '../../api/faceView/faceView'
import { ReportTypes, ProductIds } from '../../api/apiConstants'
import { onDownloadSuccess } from '../../api/shared/downloadFile'

type FaceViewProps = {
  setShow: (value: boolean) => void
  initialState: { [key: string]: any }
  setInitialState: React.Dispatch<React.SetStateAction<any>>
  setIsError: (arg0: boolean) => void
  setErrorMessage: (value: string) => void
  KYCAccess: boolean
}

const FaceView: React.FC<FaceViewProps> = ({
  setShow,
  initialState,
  setInitialState,
  setIsError,
  KYCAccess,
  setErrorMessage,
}) => {
  const [idNumber, setIdNumber] = useState('')
  const [reference, setReference] = useState('')
  const [isDisabled, setIsDisabled] = useState(true)

  useEffect(() => {
    const isValidIdNumber = validateIdNumber(idNumber)
    setIsDisabled(!isValidIdNumber || !KYCAccess)
  }, [idNumber])

  const updateInitialState = () => {
    setInitialState({
      ...initialState,
      faceView: {
        idNumber: idNumber,
        reference: reference,
        price: PRICELIST.FACEVIEW,
      },
    })
  }

  const body = {
    productId: ProductIds.FaceViewReport.productId,
    identityNumber: idNumber,
    reference: reference,
    reportType: ReportTypes.FaceViewReport.reportTypeId,
  }

  const { mutate: loadFaceviewReport, isLoading: isLoadingFromMutate } =
    useMutation('LoadFaceViewReport', async () => {
      try {
        const response = await LoadReport({ body })
        if (response === null) {
          alert(`No results found for: ${idNumber}`)
        }
        const downloadBody = {
          reportType: ReportTypes.FaceViewReport.reportTypeId,
          reportId: response.data.reportId,
        }
        const downloadResponse = await DownloadFaceViewReport({
          body: downloadBody,
        })
        if (downloadResponse) {
          onDownloadSuccess(downloadResponse)
          setIdNumber('')
        }
      } catch (error) {
        if (isHttpResponseError(error)) {
          setIsError(error.response.status !== 404)
          setErrorMessage(API_ERROR_MESSAGE)
        }
      }
    })

  function isHttpResponseError(
    error: any,
  ): error is { response: { status: number } } {
    return (
      typeof error === 'object' &&
      error !== null &&
      'response' in error &&
      'status' in error.response
    )
  }
  const validateFields = () => {
    const missingFields = []
    if (!validateIdNumber(idNumber)) {
      missingFields.push('ID number')
    }
    return missingFields
  }
  const handleSearch = () => {
    const missingFields = validateFields()
    if (missingFields.length > 0) {
      return alert(
        'Please fill in the following fields: ' + missingFields.join(', '),
      )
    }
    updateInitialState()
    loadFaceviewReport()
  }

  return (
    <ScreenLayout
      headerText="Faceview"
      subHeaderText="Home Affairs Photo ID Search."
      price={PRICELIST.FACEVIEW}
      sampleReport="FaceView.pdf"
    >
      {isLoadingFromMutate && (
        <>
          <div className="spinner-container">
            <Spinner
              as="span"
              animation="border"
              variant="dark"
              role="status"
              aria-hidden="true"
            ></Spinner>
            <span> </span>
          </div>
        </>
      )}
      <div className="mb-3">
        {KYCAccess !== true && <p className="lbl-red">{RESTRICTED_REPORT}</p>}
        <FormControlWithToolTip
          required
          onChangeHandler={setIdNumber}
          value={idNumber}
          placeHolderText="Unique identity number"
          toolTipText="Enter your unique Id number"
        />
      </div>
      {idNumber !== '' && !validateIdNumber(idNumber) ? (
        <Alert variant="danger" transition={false}>
          {INVALID_ID_WARNING}
        </Alert>
      ) : null}
      <FormInput
        className="mb-3"
        placeHolderText="Search reference (optional)"
        onChangeHandler={setReference}
        value={reference}
      />
      <FooterButtons handleSearch={handleSearch} setShow={setShow} />
    </ScreenLayout>
  )
}
export default FaceView
